/**
 * 電子報群發 (newsletter add-on): the email a brand builds in the portal editor (a list of blocks, like
 * Mailchimp / Klaviyo), its request bodies, and the one renderer both the editor preview and the sending
 * worker use, so the brand sees what members get. The HTML follows what email programs show reliably:
 * tables for layout, inline styles, one 600px column whose two-column rows stack on phones, absolute image
 * URLs, no scripts. Text is plain with two marks: **bold** and [link text](https://…); {name} is the member's name.
 */
import { z } from 'zod';
import type { SocialLinks } from './contract.ts';
import type { Lang } from './i18n.ts';
import { socialUrl } from './social.ts';

export const NEWSLETTER_BLOCK_TYPES = ['heading', 'text', 'image', 'button', 'divider', 'spacer', 'columns', 'offer', 'social'] as const;
export type NewsletterBlockType = (typeof NEWSLETTER_BLOCK_TYPES)[number];

export const NEWSLETTER_MAX_BLOCKS = 60;
export const NEWSLETTER_SCHEDULE_MAX_DAYS = 90;
/** Addresses per test send, and test sends per brand per hour. */
export const NEWSLETTER_TEST_MAX = 5;
export const NEWSLETTER_TESTS_PER_HOUR = 20;
/** Emails per brand per run of the 2-minute cron (the provider's own limits may slow it further). */
export const NEWSLETTER_BATCH = 200;

export type NewsletterAlign = 'left' | 'center' | 'right';

/** One side of a 兩欄 block: a picture or text, optionally a link. */
export interface NewsletterColumn {
  kind: 'image' | 'text';
  imageKey: string | null;
  text: string;
  link: string;
}

export type NewsletterSocial = keyof SocialLinks;

export type NewsletterBlock =
  | { id: string; type: 'heading'; text: string; align: NewsletterAlign; size: 'lg' | 'md' }
  | { id: string; type: 'text'; text: string; align: NewsletterAlign }
  /** full = edge to edge, content = the text column, small = 200px centred (a logo). */
  | { id: string; type: 'image'; imageKey: string | null; alt: string; link: string; width: 'full' | 'content' | 'small' }
  | { id: string; type: 'button'; label: string; url: string; align: NewsletterAlign; color: string | null }
  | { id: string; type: 'divider' }
  | { id: string; type: 'spacer'; height: number }
  | { id: string; type: 'columns'; left: NewsletterColumn; right: NewsletterColumn }
  /** 優惠券／推廣活動卡: copied from the coupon or promotion when picked, so the email does not change with it later. */
  | {
      id: string;
      type: 'offer';
      source: 'reward' | 'promotion' | 'custom';
      refId: string | null;
      imageKey: string | null;
      badge: string;
      title: string;
      description: string;
      buttonLabel: string;
      url: string;
    }
  | { id: string; type: 'social'; links: Partial<Record<NewsletterSocial, string>> };

export interface NewsletterStyle {
  /** Around the email. */
  background: string;
  /** The email itself. */
  contentBackground: string;
  textColor: string;
  /** Buttons, links and the coupon card. */
  primary: string;
  font: 'sans' | 'serif';
}

export interface NewsletterDesign {
  version: 1;
  style: NewsletterStyle;
  blocks: NewsletterBlock[];
  /** 頁尾: the brand's own lines (address, contact). The consent line and unsubscribe link always follow. */
  footer: string;
}

// ---------------------------------------------------------------- request bodies

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/** Web, email and phone links only (never javascript: and the like); empty = no link. */
export const isNewsletterLink = (v: string) => v === '' || /^(https?:\/\/|mailto:|tel:)[^\s"<>]+$/i.test(v);
const link = z.string().trim().max(1000).refine(isNewsletterLink, 'must start with https://, mailto: or tel:');
const imageKey = z.string().min(1).max(300).nullable();
const blockId = z.string().min(1).max(40);
const align = z.enum(['left', 'center', 'right']);
const column = z.object({ kind: z.enum(['image', 'text']), imageKey, text: z.string().max(3000), link });

export const newsletterBlockSchema = z.discriminatedUnion('type', [
  z.object({ id: blockId, type: z.literal('heading'), text: z.string().max(300), align, size: z.enum(['lg', 'md']) }),
  z.object({ id: blockId, type: z.literal('text'), text: z.string().max(5000), align }),
  z.object({ id: blockId, type: z.literal('image'), imageKey, alt: z.string().max(200), link, width: z.enum(['full', 'content', 'small']) }),
  z.object({ id: blockId, type: z.literal('button'), label: z.string().max(60), url: link, align, color: hex.nullable() }),
  z.object({ id: blockId, type: z.literal('divider') }),
  z.object({ id: blockId, type: z.literal('spacer'), height: z.number().int().min(8).max(96) }),
  z.object({ id: blockId, type: z.literal('columns'), left: column, right: column }),
  z.object({
    id: blockId,
    type: z.literal('offer'),
    source: z.enum(['reward', 'promotion', 'custom']),
    refId: z.string().max(64).nullable(),
    imageKey,
    badge: z.string().max(40),
    title: z.string().max(200),
    description: z.string().max(1000),
    buttonLabel: z.string().max(60),
    url: link,
  }),
  z.object({
    id: blockId,
    type: z.literal('social'),
    links: z.record(z.enum(['facebook', 'instagram', 'whatsapp', 'youtube', 'threads', 'xiaohongshu', 'linkedin', 'website']), z.string().trim().max(300)),
  }),
]);

export const newsletterDesignSchema = z.object({
  version: z.literal(1),
  style: z.object({ background: hex, contentBackground: hex, textColor: hex, primary: hex, font: z.enum(['sans', 'serif']) }),
  blocks: z.array(newsletterBlockSchema).max(NEWSLETTER_MAX_BLOCKS),
  footer: z.string().max(2000),
});

/** A draft: everything may still be empty; sending checks the rest. */
export const newsletterSaveSchema = z.object({
  subject: z.string().trim().max(150),
  /** 預覽文字: the line inboxes show after the subject. */
  preheader: z.string().trim().max(150),
  design: newsletterDesignSchema,
});
export type NewsletterSaveInput = z.infer<typeof newsletterSaveSchema>;

export const newsletterSendSchema = z.object({
  /** Only members of this segment (still only those who agreed to marketing email); absent = everyone. */
  segmentId: z.string().min(1).max(64).nullable().optional(),
  /** When to send; null or absent = now. The segment is read when it goes out. */
  sendAt: z.string().datetime({ offset: true }).nullable().optional(),
});

export const newsletterTestSchema = z.object({
  emails: z.array(z.string().trim().toLowerCase().email().max(254)).min(1).max(NEWSLETTER_TEST_MAX),
});

export const newsletterTemplateSchema = z.object({
  name: z.string().trim().min(1).max(60),
  design: newsletterDesignSchema,
});

/** 電子報加額 (admin portal): extra emails for one Hong Kong month, this month or a later one. */
export const newsletterTopUpSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM'),
  emails: z.number().int().min(1).max(10_000_000),
  note: z.string().trim().max(200),
});
export type NewsletterTopUpInput = z.infer<typeof newsletterTopUpSchema>;

export interface NewsletterTopUp {
  id: string;
  month: string;
  emails: number;
  note: string;
  createdBy: string;
  createdAt: string;
}

// ---------------------------------------------------------------- responses

export type NewsletterStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';

/** Members picked when it went out, by what happened to their email. */
export interface NewsletterReport {
  queued: number;
  sent: number;
  failed: number;
  /** Withdrew consent (or left) between the start of the send and their turn. */
  skipped: number;
  /** Used the unsubscribe link in this newsletter. */
  unsubscribed: number;
}

export interface NewsletterSummary {
  id: string;
  subject: string;
  preheader: string;
  status: NewsletterStatus;
  /** Scheduled time, or when it started going out. */
  sendAt: string | null;
  sentAt: string | null;
  finishedAt: string | null;
  segmentId: string | null;
  segmentName: string | null;
  /** Members it was sent to (agreed to marketing email, in the audience). */
  recipients: number;
  /** In the audience with an email address but no consent to marketing email. */
  skippedNoConsent: number;
  report: NewsletterReport;
  /** Why sending paused (e.g. the provider's daily limit); it carries on by itself. */
  note: string | null;
  error: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface NewsletterAdmin extends NewsletterSummary {
  design: NewsletterDesign;
}

export interface NewsletterTemplate {
  id: string;
  name: string;
  design: NewsletterDesign;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * 電子報額度 this Hong Kong calendar month: the plan's (or the brand's own) monthly limit, 加額 added in the
 * admin portal for this month, and the emails sent (test emails are not counted).
 */
export interface NewsletterQuota {
  /** null = no limit. */
  limit: number | null;
  extra: number;
  sent: number;
  /** limit + extra - sent, at least 0; null = no limit. */
  left: number | null;
}

export interface NewsletterOverview {
  /** Active members with an email address who agreed to marketing email. */
  reachable: number;
  quota: NewsletterQuota;
  scheduled: number;
  sender: { name: string; email: string; replyTo: string | null };
}

export type NewsletterRecipientStatus = 'queued' | 'sent' | 'failed' | 'skipped';

export interface NewsletterRecipientRow {
  memberId: string;
  memberNo: string | null;
  name: string | null;
  email: string | null;
  status: NewsletterRecipientStatus;
  error: string | null;
  sentAt: string | null;
  unsubscribedAt: string | null;
}

// ---------------------------------------------------------------- rendering

export interface NewsletterRenderContext {
  /** Absolute URL of an uploaded image (the member site's /media/ in emails, the portal's in the editor). */
  imageUrl: (key: string) => string;
  /** The member's own unsubscribe link; null in the editor preview. */
  unsubscribeUrl: string | null;
  brandName: string;
  /** The brand's default language: the consent line under the email is in it (and in English). */
  lang: Lang;
  /** {name} etc. in the text; unknown placeholders stay as typed. */
  vars?: Record<string, string>;
}

const FONTS = {
  sans: "Helvetica,Arial,'PingFang HK','Noto Sans TC','Microsoft JhengHei',sans-serif",
  serif: "Georgia,'Times New Roman','Noto Serif TC',PMingLiU,serif",
};
const WIDTH = 600;
const PAD = 32;

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const color = (c: string | null | undefined, fallback: string) => (c && /^#[0-9a-fA-F]{6}$/.test(c) ? c : fallback);

/** Readable text colour (white or near-black) on top of `hex`. */
export function textOn(hex: string): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return '#ffffff';
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.42 ? '#1b1f1d' : '#ffffff';
}

const href = (url: string) => (url && isNewsletterLink(url) ? url : null);
const fill = (html: string, vars: Record<string, string> = {}) => html.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? esc(vars[k]!) : m));

/**
 * Plain text to HTML: escaped, **bold**, [text](link), paragraphs on blank lines. Placeholders are filled last
 * so a member's name can never turn into markup.
 */
function richText(text: string, linkColor: string, vars?: Record<string, string>): string {
  const paragraphs = text.trim().split(/\n{2,}/).filter(Boolean);
  return paragraphs
    .map((p, i) => {
      let h = esc(p)
        .replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (m, label: string, url: string) => {
          const raw = url.replace(/&amp;/g, '&');
          return isNewsletterLink(raw) && raw ? `<a href="${esc(raw)}" target="_blank" style="color:${linkColor};text-decoration:underline">${label}</a>` : m;
        })
        .replace(/\*\*([^*\n]+)\*\*/g, (_m, b: string) => `<strong>${b}</strong>`)
        .replace(/\n/g, '<br>');
      h = fill(h, vars);
      return `<p style="margin:0${i < paragraphs.length - 1 ? ' 0 14px' : ''}">${h}</p>`;
    })
    .join('');
}

/** Text without the marks, for the plain-text part. */
function plain(text: string, vars?: Record<string, string>): string {
  return text
    .replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (_m, label: string, url: string) => `${label} (${url})`)
    .replace(/\*\*([^*\n]+)\*\*/g, (_m, b: string) => b)
    .replace(/\{(\w+)\}/g, (m, k: string) => vars?.[k] ?? m)
    .trim();
}

function button(label: string, url: string, bg: string, align: NewsletterAlign, font: string): string {
  const target = href(url);
  const fg = textOn(bg);
  const inner = `<a href="${esc(target ?? '#')}" target="_blank" style="display:inline-block;padding:13px 28px;font-family:${font};font-size:16px;font-weight:bold;line-height:1.2;color:${fg};text-decoration:none;border-radius:6px">${esc(label || ' ')}</a>`;
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${align}" style="margin:${align === 'center' ? '0 auto' : '0'}"><tr><td align="center" bgcolor="${bg}" style="border-radius:6px;background:${bg}">${inner}</td></tr></table>`;
}

function image(key: string | null, alt: string, width: number, ctx: NewsletterRenderContext, link: string, radius = 0, centre = false): string {
  if (!key) {
    return `<div style="background:#e5e7eb;color:#6b7280;font-size:13px;text-align:center;padding:48px 12px;border-radius:${radius}px">${ctx.unsubscribeUrl === null ? '（未選擇圖片）' : ''}</div>`;
  }
  const img = `<img src="${esc(ctx.imageUrl(key))}" width="${width}" alt="${esc(alt)}" style="display:block;${centre ? 'margin:0 auto;' : ''}width:100%;max-width:${width}px;height:auto;border:0;outline:none;text-decoration:none;border-radius:${radius}px">`;
  const target = href(link);
  return target ? `<a href="${esc(target)}" target="_blank" style="text-decoration:none">${img}</a>` : img;
}

const SOCIAL_LABELS: Record<NewsletterSocial, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  youtube: 'YouTube',
  threads: 'Threads',
  xiaohongshu: '小紅書',
  linkedin: 'LinkedIn',
  website: '網站',
};
export const NEWSLETTER_SOCIALS = Object.keys(SOCIAL_LABELS) as NewsletterSocial[];
export const newsletterSocialLabel = (k: NewsletterSocial) => SOCIAL_LABELS[k];

/** One block as a table row of the email. */
export function renderNewsletterBlock(b: NewsletterBlock, design: NewsletterDesign, ctx: NewsletterRenderContext): string {
  const s = design.style;
  const font = FONTS[s.font] ?? FONTS.sans;
  const ink = color(s.textColor, '#1f2937');
  const primary = color(s.primary, '#074736');
  const row = (inner: string, pad = `8px ${PAD}px`, extra = '') => `<tr><td class="lp-pad" style="padding:${pad};font-family:${font};color:${ink};${extra}">${inner}</td></tr>`;
  switch (b.type) {
    case 'heading': {
      const size = b.size === 'lg' ? 26 : 20;
      return row(`<h${b.size === 'lg' ? 1 : 2} style="margin:0;font-family:${font};font-size:${size}px;line-height:1.3;font-weight:bold;color:${ink};text-align:${b.align}">${fill(esc(b.text), ctx.vars)}</h${b.size === 'lg' ? 1 : 2}>`, `16px ${PAD}px 4px`);
    }
    case 'text':
      return row(`<div style="font-size:16px;line-height:1.65;text-align:${b.align}">${richText(b.text, primary, ctx.vars)}</div>`);
    case 'image':
      if (b.width === 'full') return row(image(b.imageKey, b.alt, WIDTH, ctx, b.link), '0');
      if (b.width === 'small') return row(image(b.imageKey, b.alt, 200, ctx, b.link, 0, true), `16px ${PAD}px`, 'text-align:center');
      return row(image(b.imageKey, b.alt, WIDTH - PAD * 2, ctx, b.link, 8), `12px ${PAD}px`);
    case 'button':
      return row(button(b.label, b.url, color(b.color, primary), b.align, font), `14px ${PAD}px`, `text-align:${b.align}`);
    case 'divider':
      return row(`<div style="border-top:1px solid #e5e7eb;font-size:0;line-height:0">&nbsp;</div>`, `16px ${PAD}px`);
    case 'spacer':
      return `<tr><td style="height:${b.height}px;font-size:0;line-height:0">&nbsp;</td></tr>`;
    case 'columns': {
      const colWidth = (WIDTH - 2 * (PAD - 8)) / 2 - 16;
      const side = (c: NewsletterColumn) =>
        c.kind === 'image'
          ? image(c.imageKey, '', colWidth, ctx, c.link, 8)
          : `<div style="font-size:15px;line-height:1.6">${richText(c.text, primary, ctx.vars)}</div>`;
      const cell = (c: NewsletterColumn) =>
        `<td class="lp-col" width="50%" valign="top" style="width:50%;padding:0 8px;vertical-align:top;font-family:${font};color:${ink}">${side(c)}</td>`;
      return row(`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${cell(b.left)}${cell(b.right)}</tr></table>`, `12px ${PAD - 8}px`);
    }
    case 'offer': {
      const pic = b.imageKey ? `<tr><td style="padding:0">${image(b.imageKey, b.title, WIDTH - PAD * 2 - 4, ctx, b.url)}</td></tr>` : '';
      const badge = b.badge ? `<div style="margin:0 0 10px"><span style="display:inline-block;padding:4px 12px;border-radius:999px;background:${primary};color:${textOn(primary)};font-size:12px;font-weight:bold;letter-spacing:.5px">${esc(b.badge)}</span></div>` : '';
      const desc = b.description ? `<div style="margin:8px 0 0;font-size:14px;line-height:1.6;color:#4b5563">${richText(b.description, primary, ctx.vars)}</div>` : '';
      const cta = b.buttonLabel ? `<div style="margin:18px 0 0">${button(b.buttonLabel, b.url, primary, 'center', font)}</div>` : '';
      return row(
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border:2px dashed ${primary};border-radius:12px;border-collapse:separate;overflow:hidden;background:#ffffff">${pic}<tr><td align="center" style="padding:22px 20px;text-align:center;font-family:${font};color:${ink}">${badge}<div style="font-size:20px;line-height:1.35;font-weight:bold">${fill(esc(b.title), ctx.vars)}</div>${desc}${cta}</td></tr></table>`,
        `12px ${PAD}px`,
      );
    }
    case 'social': {
      const links = NEWSLETTER_SOCIALS.flatMap((k) => {
        const url = socialUrl(k, b.links[k]);
        return url && /^https:\/\//i.test(url)
          ? [`<a href="${esc(url)}" target="_blank" style="display:inline-block;margin:4px;padding:7px 14px;border-radius:999px;background:${primary};color:${textOn(primary)};font-size:13px;font-weight:bold;text-decoration:none">${SOCIAL_LABELS[k]}</a>`]
          : [];
      });
      return row(links.length ? links.join(' ') : ctx.unsubscribeUrl === null ? '<span style="color:#9ca3af;font-size:13px">（未填寫社交平台）</span>' : '', `12px ${PAD}px`, 'text-align:center');
    }
  }
}

const CONSENT_LINE: Record<Lang, (brand: string) => [string, string]> = {
  'zh-Hant': (b) => [`你收到這封電郵，是因為你已同意接收「${b}」的推廣電郵。如不想再收到，請`, '取消訂閱'],
  'zh-Hans': (b) => [`你收到这封电邮，是因为你已同意接收「${b}」的推广电邮。如不想再收到，请`, '取消订阅'],
  en: (b) => [`You are receiving this email because you agreed to receive marketing emails from ${b}. To stop them, `, 'unsubscribe'],
};

/** 頁尾 row: the brand's own lines, then the consent line with the unsubscribe link. */
export function renderNewsletterFooter(design: NewsletterDesign, ctx: NewsletterRenderContext): string {
  const font = FONTS[design.style.font] ?? FONTS.sans;
  const unsub = esc(ctx.unsubscribeUrl ?? '#');
  const line = (lang: Lang) => {
    const [before, label] = CONSENT_LINE[lang](ctx.brandName);
    return `<p style="margin:8px 0 0">${esc(before)}<a href="${unsub}" target="_blank" style="color:#6b7280;text-decoration:underline">${label}</a>${lang === 'en' ? '.' : '。'}</p>`;
  };
  const own = design.footer.trim() ? `<div style="margin:0 0 4px">${richText(design.footer, '#6b7280', ctx.vars)}</div>` : '';
  // The statement in the brand's language, and in English too (UEMO asks for both where the message is in Chinese).
  return `<tr><td class="lp-pad" style="padding:24px ${PAD}px 28px;border-top:1px solid #e5e7eb;font-family:${font};font-size:12px;line-height:1.6;color:#6b7280;text-align:center">${own}${line(ctx.lang)}${ctx.lang === 'en' ? '' : line('en')}</td></tr>`;
}

/** The whole email, ready to send (or to show in the preview frame). */
export function renderNewsletterHtml(design: NewsletterDesign, meta: { subject: string; preheader: string }, ctx: NewsletterRenderContext): string {
  const s = design.style;
  const bg = color(s.background, '#f4f4f5');
  const card = color(s.contentBackground, '#ffffff');
  const rows = design.blocks.map((b) => renderNewsletterBlock(b, design, ctx)).join('');
  // Inbox preview line, then filler so the start of the body does not show after it.
  const preheader = meta.preheader
    ? `<div style="display:none;max-height:0;max-width:0;overflow:hidden;opacity:0;mso-hide:all">${esc(meta.preheader)}${'&#8199;&#847; '.repeat(40)}</div>`
    : '';
  return `<!doctype html><html lang="${ctx.lang}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="x-apple-disable-message-reformatting"><meta name="color-scheme" content="light"><title>${esc(meta.subject)}</title>
<style>body{margin:0;padding:0}img{-ms-interpolation-mode:bicubic}a{color:inherit}@media only screen and (max-width:620px){.lp-col{display:block!important;width:100%!important;padding:0 0 16px!important}.lp-pad{padding-left:18px!important;padding-right:18px!important}}</style></head>
<body style="margin:0;padding:0;background:${bg}">${preheader}<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${bg}" style="background:${bg}"><tr><td align="center" style="padding:24px 8px">
<!--[if mso]><table role="presentation" width="${WIDTH}" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${card}" style="max-width:${WIDTH}px;background:${card};border-radius:10px;border-collapse:separate;overflow:hidden">${rows}${renderNewsletterFooter(design, ctx)}</table>
<!--[if mso]></td></tr></table><![endif]-->
</td></tr></table></body></html>`;
}

/** The plain-text part (some email programs and spam filters read it). */
export function renderNewsletterText(design: NewsletterDesign, ctx: NewsletterRenderContext): string {
  const parts: string[] = [];
  for (const b of design.blocks) {
    if (b.type === 'heading' || b.type === 'text') parts.push(plain(b.text, ctx.vars));
    else if (b.type === 'button' && b.label) parts.push(href(b.url) ? `${b.label}: ${b.url}` : b.label);
    else if (b.type === 'image' && b.alt) parts.push(b.alt);
    else if (b.type === 'columns') parts.push(...[b.left, b.right].flatMap((c) => (c.kind === 'text' ? [plain(c.text, ctx.vars)] : [])));
    else if (b.type === 'offer') parts.push([b.badge, plain(b.title, ctx.vars), plain(b.description, ctx.vars), href(b.url) ? `${b.buttonLabel || b.url}: ${b.url}` : ''].filter(Boolean).join('\n'));
    else if (b.type === 'divider') parts.push('—');
  }
  if (design.footer.trim()) parts.push(plain(design.footer, ctx.vars));
  const consent = (lang: Lang) => CONSENT_LINE[lang](ctx.brandName).join(' ') + `: ${ctx.unsubscribeUrl ?? ''}`;
  parts.push(ctx.lang === 'en' ? consent('en') : `${consent(ctx.lang)}\n${consent('en')}`);
  return parts.filter(Boolean).join('\n\n');
}

/** Upload keys the design points at (checked to belong to the brand when it is saved). */
export function newsletterImageKeys(design: NewsletterDesign): string[] {
  const keys: Array<string | null> = [];
  for (const b of design.blocks) {
    if (b.type === 'image' || b.type === 'offer') keys.push(b.imageKey);
    if (b.type === 'columns') keys.push(b.left.imageKey, b.right.imageKey);
  }
  return keys.filter((k): k is string => !!k);
}

/** PNG, JPG and GIF show in every common email program; Gmail drops SVG and older Outlook drops WebP. */
export const isEmailSafeImage = (key: string) => /\.(png|jpe?g|gif)$/i.test(key);

/** Pictures some email programs will not show (sending is still allowed). */
export const newsletterImageWarnings = (design: NewsletterDesign) => newsletterImageKeys(design).filter((k) => !isEmailSafeImage(k)).length;

/** What still stops a draft from going out (empty = ready). */
export function newsletterProblems(input: { subject: string; design: NewsletterDesign }): string[] {
  const out: string[] = [];
  if (!input.subject.trim()) out.push('請填寫電郵主旨');
  if (!input.design.blocks.length) out.push('電子報未有任何內容');
  for (const b of input.design.blocks) {
    if (b.type === 'button' && (!b.label.trim() || !b.url)) out.push('有按鈕未填寫文字或連結');
    if (b.type === 'image' && !b.imageKey) out.push('有圖片區塊未選擇圖片');
    if (b.type === 'offer' && !b.title.trim()) out.push('有優惠卡未填寫標題');
  }
  return [...new Set(out)];
}
